import { _ } from "../../polyglot/polyglot.js";
import { isLastDayOfMonth } from "../../helpers/date-util.js";
import AbstractWizardStep from "../AbstractWizardStep.js";
import ConfigureImportDataConfigurationWidget from "./ConfigureImportDataConfigurationWidget.js";
import ConfigureImportDataSummaryWidget from "./ConfigureImportDataSummaryWidget.js";

/**
 * `ConfigureImportDataStep` is the final step of the import
 * process. Its role is to display a summary of the import data and to
 * let the user configure the import data just before the data is
 * added to the company.
 *
 * @param {boolean} [selectOnlyLatestFiscalYear] - When this option is true, only
 *   the latest fiscal year is selected, otherwise all the fiscal years will be
 *   selected.
 */
export default class ConfigureImportDataStep extends AbstractWizardStep {
	constructor({
		wantExtendedActions,
		wantChartOfAccountsConfiguration,
		wantsBudgetPicker,
		selectOnlyLatestFiscalYear = false,
	} = {}) {
		super(...arguments);
		this._wantExtendedActions = wantExtendedActions;

		this._wantChartOfAccountsConfiguration =
			wantChartOfAccountsConfiguration;

		this._wantsBudgetPicker = wantsBudgetPicker;
		this._selectOnlyLatestFiscalYear = selectOnlyLatestFiscalYear;
	}

	validate(...rest) {
		this._configurationWidget.validate(...rest);
	}

	getRenderable() {
		return (html) => {
			html.div(
				{ klass: "data-section" },
				html.h2({ klass: "import-sub-title" }, _("Import summary")),
				this._summaryWidget,
			);

			html.div(
				{ klass: "data-section" },
				html.h2(
					{ klass: "import-sub-title" },
					_("Import configuration"),
				),
				this._configurationWidget,
			);
		};
	}

	_isCompleted() {
		return this._isValidBusinessIdentityCode();
	}

	_doStart() {
		const importData = this._context.importData;
		const chartsOfAccounts = this._context.chartsOfAccounts;

		this._configurationWidget = new ConfigureImportDataConfigurationWidget({
			importData,
			canCreateLightCompany: this._context.canCreateLightCompany,
			isExistingCompany: this._context.isExistingCompany,
			isExistingLightCompany: this._context.isExistingLightCompany,
			wantExtendedActions: this._wantExtendedActions,
			wantChartOfAccountsConfiguration:
				this._wantChartOfAccountsConfiguration,
			wantsBudgetPicker: this._wantsBudgetPicker,
			chartsOfAccounts,
			selectOnlyLatestFiscalYear: this._selectOnlyLatestFiscalYear,
		});

		this._summaryWidget = new ConfigureImportDataSummaryWidget({
			importData,
		});

		this._validate();
	}

	_validate() {
		this._ckeckForValidBusinessIdentityCode();
		this._checkForCorrectFiscalYearsEnds();
	}

	_ckeckForValidBusinessIdentityCode() {
		if (this._isValidBusinessIdentityCode()) {
			return;
		}

		this.addSubmitError(
			_(
				"You are importing data from a different company. We blocked it as it is usually done by mistake.",
			),
		);
	}

	_checkForCorrectFiscalYearsEnds() {
		const endsOfFiscalYears =
			this._context.importData.importCompanyInfo.fiscalYears.map(
				(fiscalYear) => fiscalYear.end,
			);

		if (endsOfFiscalYears.some((endDate) => !isLastDayOfMonth(endDate))) {
			this.addSubmitError(
				_("Year end is not a month end. Data cannot be imported."),
			);
		}
	}

	_doSubmit(...rest) {
		this._context.configuredData = {
			isLightCompany:
				this._configurationWidget.shouldImportAsLightCompany(),
			lastTransactionDate:
				this._configurationWidget.getLastTransactionDate(),
			lastActiveTransactionDate:
				this._configurationWidget.getLastActiveDate(),
			budgetOption: this._configurationWidget.getBudget(),

			fiscalYearOptions: this._configurationWidget
				.getFiscalYears()
				.map((year) => ({
					importId: year.importId,
					selectedDimensions: year.selectedDimensions,
					currencyCultureName:
						this._configurationWidget.getCurrency(),
					chartOfAccountsOption:
						this._configurationWidget.getChartOfAccounts(),
				})),
		};

		this.validate({ collector: this._submitExceptions });
		super._doSubmit(...rest);
	}

	_isValidBusinessIdentityCode() {
		const forcedBusinessIdentityCode =
			this._context.forcedBusinessIdentifyCode;
		const importBusinessIdentityCode =
			this._context.importData.importCompanyInfo.businessIdentityCode;

		if (!forcedBusinessIdentityCode) {
			return true;
		}

		return forcedBusinessIdentityCode === importBusinessIdentityCode;
	}
}
